import { KeyboardEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Controller, useFormContext } from "react-hook-form";

import { RequestFormState } from "pages/shipper/RequestForm/RequestFormState";
import Radio from "components/atoms/Radio/Radio";
import FormField from "components/molecules/FormField/FormField";
import {
  getSpecialRequirementValidationMessage,
  isValidSpecialRequirementAdditionalInformation,
  setSpecialRequirementAdditionalInformationValue,
} from "helpers/validationHelpers";
import NumericSpinnerField from "./NumericSpinnerField";
import FormItemInfo from "../Common/FormItemInfo";
import "./serviceForm.scss";

type SpecialRequirementDataType = "string" | "integer" | "double" | "email";

type SpecialRequirementDefinition = {
  label: string;
  value: string;
  data_type?: SpecialRequirementDataType;
  additional_information_required?: boolean;
};

type ServiceRequirementFormProps = {
  state: RequestFormState;
};

function ServiceRequirementForm(props: ServiceRequirementFormProps) {
  const intl = useIntl();
  const { state } = props;
  const { watch, setValue, control } = useFormContext();

  const request_service_requirement_values: Record<string, SpecialRequirementDefinition> =
    state.service?.request_service_requirement_values ?? {};

  const radioOptions = [
    {
      value: "true",
      label: intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" }),
    },
    {
      value: "false",
      label: intl.formatMessage({ id: "general.no", defaultMessage: "No" }),
    },
  ];

  const clearAdditionalInformation = (requirementKey: string) => {
    setValue(`service.special_requirements.${requirementKey}.additional_information`, "");
  };

  const handleRequirementValueChange = (requirementKey: string, selectedValue: string) => {
    if (selectedValue === "false") {
      clearAdditionalInformation(requirementKey);
    }
  };

  const handleIntegerKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    const { key, currentTarget } = event;
    if (key.length !== 1) return;

    if (/\d/.test(key)) return;

    if (key === "-" && currentTarget.selectionStart === 0 && !currentTarget.value.includes("-")) return;

    event.preventDefault();
  };

  const handleDoubleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    const { key, currentTarget } = event;
    if (key.length !== 1) return;

    if (/\d/.test(key)) return;

    if (key === "-" && currentTarget.selectionStart === 0 && !currentTarget.value.includes("-")) return;

    if (key === "." && !currentTarget.value.replace("-", "").includes(".")) return;

    event.preventDefault();
  };

  return (
    <div className="form-freight-mode flex-row mt-24 mb-8">
      <div className="w-200">
        <FormItemInfo
          labelId="new-request.organisational-details.requirements"
          label="Requirements"
          descId="new-request.organisational-details.requirementsInfo"
          description="Provide info about requirements"
        />
      </div>
      <div>
        {Object.values(request_service_requirement_values).map((item) => {
          const dataType: SpecialRequirementDataType = item.data_type ?? "string";
          const fieldId = `service.special_requirements.${item.value}.additional_information`;
          const isYes = watch(`service.special_requirements.${item.value}.value`) === "true";
          const isAdditionalInfoRequired = item.additional_information_required ?? true;
          const validateAdditionalInformation = (value: string) =>
            isValidSpecialRequirementAdditionalInformation(value, dataType, isAdditionalInfoRequired) ||
            getSpecialRequirementValidationMessage(dataType);

          const placeholder = intl.formatMessage({
            id: "general.additionalInformation",
            defaultMessage: "Additional information",
          });
          const testId = `form-field-${fieldId.replace(/[.[\]]+/g, "-").replace(/-+$/g, "")}`;

          return (
            <div key={item.value} className="mb-xl">
              <div>
                <div className="font-b4">{item.label}</div>
                <Radio
                  options={radioOptions}
                  name={`service.special_requirements.${item.value}.value`}
                  customOption
                  className="border-0 width-fit pt-sm pb-none"
                  labelClassName="gap-sm"
                  checkmarkClassName="mr-none"
                  eventContext="Task Dialog"
                  customColClass="pl-none pr-4xl"
                  defaultChecked={watch(`service.special_requirements.${item.value}.value`) ?? "false"}
                  customOnChange={(selectedValue) => handleRequirementValueChange(item.value, selectedValue)}
                  required
                />
                {isYes &&
                  (dataType === "integer" || dataType === "double" ? (
                    <NumericSpinnerField
                      id="additional_information"
                      fieldId={fieldId}
                      dataType={dataType}
                      mandatory={isAdditionalInfoRequired}
                      placeholder={placeholder}
                      containerClasses="width-xs mt-md"
                      validations={{
                        setValueAs: (value: string) =>
                          setSpecialRequirementAdditionalInformationValue(value, dataType),
                        validate: validateAdditionalInformation,
                      }}
                      onKeyPress={dataType === "integer" ? handleIntegerKeyPress : handleDoubleKeyPress}
                    />
                  ) : dataType === "email" ? (
                    <Controller
                      name={fieldId}
                      control={control}
                      rules={{
                        required: isAdditionalInfoRequired,
                        validate: validateAdditionalInformation,
                      }}
                      render={({ field, fieldState }) => (
                        <div
                          className="d-flex flex-column width-xs mt-md"
                          id={fieldId}
                          data-testid={testId}
                        >
                          <input
                            id="additional_information"
                            type="text"
                            placeholder={placeholder}
                            value={field.value ?? ""}
                            className={`form-control font-b3 br-10 pt-6 pb-6 pl-10 pr-10 bg-white w-100 height-auto placeholder-light ${
                              fieldState.error ? "invalid-input" : ""
                            }`}
                            data-testid={`${testId}-input`}
                            onChange={(event) =>
                              field.onChange(
                                setSpecialRequirementAdditionalInformationValue(event.target.value, "email"),
                              )
                            }
                            onBlur={field.onBlur}
                          />
                          {fieldState.error ? (
                            <div className="d-block font-b3 invalid-feedback">
                              {fieldState.error.message ? (
                                fieldState.error.message
                              ) : (
                                <FormattedMessage
                                  id="general.requiredField"
                                  defaultMessage="This is a required field"
                                />
                              )}
                            </div>
                          ) : null}
                        </div>
                      )}
                    />
                  ) : (
                    <FormField
                      id="additional_information"
                      field_id={fieldId}
                      type="textinput"
                      mandatory={isAdditionalInfoRequired}
                      placeholder={placeholder}
                      containerClasses="width-xs mt-md"
                      validations={{
                        validate: validateAdditionalInformation,
                      }}
                    />
                  ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ServiceRequirementForm;
